import axios from 'axios';
import { http } from '../http';

export interface TipoVeiculo {
  codigo: string;
  descricao: string;
}

export interface Regional {
  codigoRegional: string;
  nomeRegional: string;
}

export interface Produto {
  codigo_produto: string;
  descricao_produto: string;
  descricao_produto_boleto: string;
  valor_produto: string;
  classificacao_produto: string;
  formato_cobranca: string;
  // flags espelhadas do SGA (at-04): 'Y'/'N'; null = ainda não descoberta pelo sync
  compulsorio?: string | null;
  padrao?: string | null;
  tipo_veiculo: TipoVeiculo;
  regionais: Regional[];
}

export const isProdutoCompulsorio = (p: { compulsorio?: string | null }) =>
  String(p.compulsorio ?? '').toUpperCase() === 'Y';

export const isProdutoPadrao = (p: { padrao?: string | null }) =>
  String(p.padrao ?? '').toUpperCase() === 'Y';

export interface ProdutoSyncExecucao {
  id: string;
  status: 'EM_ANDAMENTO' | 'CONCLUIDO' | 'FALHA' | 'INTERROMPIDO';
  origem: 'CRON' | 'MANUAL';
  fase: 'ESPELHAMENTO' | 'FLAGS';
  total: number;
  processados: number;
  percentual: number;
  sincronizadas: number;
  flagsCobertos: number;
  flagsSemPerfil: number;
  flagsFalhas: number;
  flagsChamadas: number;
  erro: string | null;
  iniciadoEm: string;
  finalizadoEm: string | null;
}

export interface ProdutoSyncStatus {
  totalProdutos: number;
  produtosComFlagDescoberta: number;
  execucao: ProdutoSyncExecucao | null;
}

export const sincronizarProdutos = async (): Promise<{ mensagem: string; runId: string }> => {
  try {
    const response = await http.get<{ mensagem: string; runId: string }>('/sga-produto/sync');
    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Erro ao disparar sincronização de produtos');
    }
    throw new Error('Erro desconhecido');
  }
};

export const getProdutoSyncStatus = async (): Promise<ProdutoSyncStatus> => {
  try {
    const response = await http.get<ProdutoSyncStatus>('/sga-produto/sync-status');
    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Erro ao consultar status do sync de produtos');
    }
    throw new Error('Erro desconhecido');
  }
};

export type ProdutoResponse = Produto[];

export const fetchSgaProdutos = async (
  codigoRegional?: string,
  codigoCooperativa?: string,
  codigoTipoVeiculo?: string,
  valorFipe?: number
): Promise<ProdutoResponse> => {
  try {
    const params = new URLSearchParams();

    if (codigoRegional) params.append('codigo_regional', codigoRegional);
    if (codigoCooperativa) params.append('codigo_cooperativa', codigoCooperativa);
    if (codigoTipoVeiculo) params.append('tipo_veiculo', codigoTipoVeiculo);
    if (valorFipe) params.append('valor_fipe', valorFipe.toString());

    const response = await http.get<ProdutoResponse>(`/sga-produto/listar?${params.toString()}`);

    return response.data;
  } catch (err) {
    const error = err as any;
    throw new Error(
      `Erro ao buscar produtos: ${error.response?.data?.message || error.message || 'Erro desconhecido'}`,
    );
  }
};

/** Situação do vínculo no SGA devolvida pelo backend (at-36). */
export interface EnvioVinculoSga {
  situacao:
    | 'SINCRONIZADO'
    | 'SEM_VEICULO_NO_SGA'
    | 'SEM_PRODUTOS'
    | 'SGA_INDISPONIVEL'
    | 'FALHA';
  adicionados: number;
  jaVinculados: number;
}

export const vincularProdutosLocal = async (
  carroId: string,
  produtos: string[]
): Promise<{ envioSga?: EnvioVinculoSga } | undefined> => {
  try {
    const { data } = await http.post('/sga-produto/vincular-produtos', {
      carroId,
      produtos,
    });

    // at-36: o backend vincula no SGA na hora e devolve o desfecho — a tela usa para
    // avisar quando ficou pendente (o cron de retry cobre).
    return data;
  } catch (error: any) {
    // Comportamento preservado: falha aqui não interrompe o fluxo do chamador.
    return undefined;
  }
};

export const vincularProdutosSga = async (
  codigoVeiculo: string,
  produtosAdicionar: string[],
  produtosRemover: string[]
): Promise<void> => {
  try {
    await http.post('/sga-produto/vincular-produto-sga', {
      codigo_veiculo: codigoVeiculo,
      produtos_adicionar: produtosAdicionar,
      produtos_remover: produtosRemover,
    });
  } catch (error: any) {
    // Comportamento preservado: falha aqui não interrompe o fluxo do chamador.
  }
};

export const buscarProdutosVinculados = async (carroId: string) => {
  try {
    const response = await http.get<{ produtos: { codigo_produto: string; descricao_produto: string, valor_produto: number }[] }>(
      `/sga-produto/listar-vinculados/${carroId}`
    );

    return response.data.produtos;
  } catch (error: any) {
    throw error;
  }
};

export const buscarProdutosDoCarro = async (carroId: string) => {
  try {
    const response = await http.get<{ produtos: { codigo_produto: string; descricao_produto: string; vinculadoAoSga: boolean }[] }>(
      `/sga-produto/produtos-carro/${carroId}`
    );

    return response.data.produtos;
  } catch (error: any) {
    throw error;
  }
};
